import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  MapPin,
  Bookmark,
  Bell,
  Sparkles,
  ArrowRight,
  ChevronLeft,
} from "lucide-react";

const STORAGE_KEY = "lienscout.welcome.seen.v1";

const STEPS = [
  {
    icon: MapPin,
    title: "Filter by state & city",
    body:
      "Head to Properties. Pick a state (e.g. Nevada, Tennessee). We enforce the top cities per state so you never wade through junk — Las Vegas alone has 47 real tax-sale parcels ready to browse.",
    cta: "Show me",
    href: "/properties",
  },
  {
    icon: Bookmark,
    title: "Save a search",
    body:
      "Set your filters, click Save Search on the Alerts page, and Lien Scout will remember the exact criteria. Great for tracking a specific ZIP, an $ range, or a jurisdiction.",
    cta: "Open Alerts",
    href: "/alerts",
  },
  {
    icon: Bell,
    title: "Get email alerts",
    body:
      "Every saved search auto-emails you the moment new records match — tax sales in Las Vegas or Chattanooga hit your inbox before they hit the courthouse steps.",
    cta: "Set up alerts",
    href: "/alerts",
  },
  {
    icon: Sparkles,
    title: "AI Deal Scores",
    body:
      "Each property gets a 0-100 Deal Score based on assessed value, tax owed, market comps, and neighborhood signals. Sort Properties by score to find the outliers fast.",
    cta: "See the data",
    href: "/properties",
  },
];

function markSeen() {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (err) {
    console.warn("welcome-modal localStorage write failed", err);
  }
}

export function hasSeenWelcome() {
  try {
    return Boolean(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}

export function resetWelcome() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function WelcomeModal({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const close = useCallback(() => {
    markSeen();
    onOpenChange(false);
  }, [onOpenChange]);

  const goTo = (href) => {
    close();
    navigate(href);
  };

  if (!open) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      data-testid="welcome-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div className="relative w-full max-w-lg bg-white border-2 border-black shadow-brutal">
        <button
          data-testid="welcome-close"
          onClick={close}
          className="absolute top-3 right-3 p-1.5 border-2 border-black bg-white hover:bg-yellow-100"
          aria-label="Close"
        >
          <X size={16} strokeWidth={2.5} />
        </button>

        <div className="flex items-center justify-center gap-2 px-6 pt-6 pb-2">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-2 flex-1 border-2 border-black ${
                i <= step ? "bg-red-600" : "bg-white"
              }`}
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="px-6 pt-4 pb-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 border-2 border-black bg-yellow-400 flex items-center justify-center shadow-brutal-sm">
              <Icon size={22} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                Step {step + 1} of {STEPS.length}
              </div>
              <h2
                id="welcome-title"
                data-testid="welcome-step-title"
                className="font-display font-bold text-2xl leading-tight"
              >
                {s.title}
              </h2>
            </div>
          </div>
          <p className="font-display text-sm leading-relaxed text-zinc-700">
            {s.body}
          </p>

          <div className="flex items-center justify-between pt-2">
            <button
              data-testid="welcome-back"
              onClick={() => setStep((n) => Math.max(0, n - 1))}
              disabled={step === 0}
              className="flex items-center gap-1 font-mono-data text-xs uppercase tracking-widest text-zinc-500 disabled:opacity-30 hover:text-black"
            >
              <ChevronLeft size={14} strokeWidth={2.5} /> Back
            </button>
            <div className="flex items-center gap-2">
              <button
                data-testid="welcome-skip"
                onClick={close}
                className="border-2 border-zinc-300 px-3 py-2 font-mono-data text-[11px] uppercase tracking-widest text-zinc-600 hover:border-black hover:text-black"
              >
                Skip tour
              </button>
              {isLast ? (
                <button
                  data-testid="welcome-finish"
                  onClick={() => goTo(s.href)}
                  className="flex items-center gap-2 border-2 border-black bg-red-600 text-white px-4 py-2 font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
                >
                  <Sparkles size={14} strokeWidth={2.5} /> {s.cta}
                </button>
              ) : (
                <button
                  data-testid="welcome-next"
                  onClick={() => setStep((n) => n + 1)}
                  className="flex items-center gap-2 border-2 border-black bg-yellow-400 px-4 py-2 font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300"
                >
                  Next <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
